// src/services/api.js
// Updated: 2026-03-09 — added fetchMedications, createMedication
// Updated: 2026-03-10 — fetchMedications returns { medications, userTimezone }
// Updated: 2026-03-10 — added fetchMedication, updateMedication, stopMedication for Piece 4
// API service for backend with Clerk authentication. Data calls require pet_id (selected pet).
// Usage: const { selectedPet } = usePet(); ... fetchScheduledMedications(date, getToken, selectedPet?.id);

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002/api';

/**
 * Fetch scheduled medications for a pet and date
 * @param {string} date - YYYY-MM-DD
 * @param {function} getToken - Clerk getToken
 * @param {string|null} petId - Pet ID (required by backend)
 */
export const fetchScheduledMedications = async (date = null, getToken, petId = null) => {
  try {
    const token = await getToken();
    const params = new URLSearchParams();
    if (date) params.append('date', date);
    if (petId) params.append('pet_id', petId);

    const response = await fetch(`${API_BASE_URL}/scheduled-medications?${params}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error(`API Error: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to fetch medications');
    }

    return data.data;
  } catch (error) {
    console.error('Error fetching medications:', error);
    throw error;
  }
};

/**
 * Update medication log status
 * @param {string} logId - The log ID to update
 * @param {string} status - New status (taken, skipped, pending)
 * @param {function} getToken - Clerk's getToken function
 */
export const updateMedicationStatus = async (logId, status, getToken) => {
  try {
    const token = await getToken();
    
    const response = await fetch(`${API_BASE_URL}/edit-medication-log/${logId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        status,
      })
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating medication status:', error);
    throw error;
  }
};

/**
 * Fetch as-needed logs for a medication on a date
 * @param {string} medicationId
 * @param {string} date - YYYY-MM-DD
 * @param {function} getToken
 * @param {string|null} petId - Pet ID (required by backend)
 */
export const fetchAsNeededLogs = async (medicationId, date, getToken, petId = null) => {
  try {
    const token = await getToken();
    const params = new URLSearchParams();
    if (petId) params.append('pet_id', petId);

    const response = await fetch(
      `${API_BASE_URL}/as-needed-logs/${medicationId}/${date}?${params}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`API Error: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching as-needed logs:', error);
    throw error;
  }
};

/**
 * Create an as-needed medication log
 * @param {string} medicationId
 * @param {string} date - YYYY-MM-DD
 * @param {function} getToken
 * @param {string|null} petId - Pet ID (required by backend)
 */
export const createAsNeededLog = async (medicationId, date, getToken, petId = null) => {
  try {
    const token = await getToken();
    const body = { medication_id: medicationId, date };
    if (petId) body.pet_id = petId;

    const response = await fetch(`${API_BASE_URL}/as-needed-logs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error creating as-needed log:', error);
    throw error;
  }
};

/**
 * Fetch daily note for a pet and date
 * @param {string} date - YYYY-MM-DD
 * @param {function} getToken
 * @param {string|null} petId - Pet ID (required by backend)
 */
export const fetchDailyNote = async (date, getToken, petId = null) => {
  try {
    const token = await getToken();
    const params = new URLSearchParams();
    params.append('date', date);
    if (petId) params.append('pet_id', petId);

    const response = await fetch(`${API_BASE_URL}/daily-notes?${params}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status}`);
    }

    const result = await response.json();
    return result.data;  // FIXED: Backend now returns { data: {...} }
  } catch (error) {
    console.error('Error fetching daily note:', error);
    throw error;
  }
};

/**
 * Update or create a daily note
 * @param {string} date - YYYY-MM-DD
 * @param {string} noteText
 * @param {function} getToken
 * @param {string|null} petId - Pet ID (required by backend)
 */
export const updateDailyNote = async (date, noteText, getToken, petId = null) => {
  try {
    const token = await getToken();
    const body = { date, note: noteText };
    if (petId) body.pet_id = petId;

    const response = await fetch(`${API_BASE_URL}/daily-notes`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status}`);
    }

    const result = await response.json();
    return result.data;  // FIXED: Backend now returns { data: {...} }
  } catch (error) {
    console.error('Error updating daily note:', error);
    throw error;
  }
};

/**
 * Fetch all active medications for a pet
 * @param {function} getToken - Clerk getToken
 * @param {string} petId - Pet ID (required)
 * @returns {Promise<Array>} Array of medication objects
 */
export const fetchMedications = async (getToken, petId) => {
  try {
    const token = await getToken();
    const params = new URLSearchParams({ pet_id: petId });

    const response = await fetch(`${API_BASE_URL}/medications?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status}`);
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to fetch medications');
    }

    return { medications: data.medications, userTimezone: data.user_timezone || null };
  } catch (error) {
    console.error('Error fetching medications:', error);
    throw error;
  }
};

/**
 * Create a new medication for a pet
 * @param {function} getToken - Clerk getToken
 * @param {string} petId - Pet ID (required)
 * @param {Object} medicationData - Medication fields (name, medication_format, etc.)
 * @returns {Promise<Object>} Created medication object
 */
export const createMedication = async (getToken, petId, medicationData) => {
  try {
    const token = await getToken();

    const response = await fetch(`${API_BASE_URL}/medications`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ ...medicationData, pet_id: petId }),
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status}`);
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to create medication');
    }

    return data.medication;
  } catch (error) {
    console.error('Error creating medication:', error);
    throw error;
  }
};

/**
 * Fetch a single medication by ID (for edit form)
 * @param {function} getToken - Clerk getToken
 * @param {string} id - Medication UUID
 * @returns {Promise<{ medication: Object, userTimezone: string }>}
 */
export const fetchMedication = async (getToken, id) => {
  try {
    const token = await getToken();
    const response = await fetch(`${API_BASE_URL}/medications/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status}`);
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to fetch medication');
    }

    return { medication: data.medication, userTimezone: data.user_timezone || null };
  } catch (error) {
    console.error('Error fetching medication:', error);
    throw error;
  }
};

/**
 * Update an existing medication
 * @param {function} getToken - Clerk getToken
 * @param {string} id - Medication UUID
 * @param {Object} medicationData - Fields to update (same shape as create)
 * @returns {Promise<Object>} Updated medication object
 */
export const updateMedication = async (getToken, id, medicationData) => {
  try {
    const token = await getToken();

    const response = await fetch(`${API_BASE_URL}/medications/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(medicationData),
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status}`);
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to update medication');
    }

    return data.medication;
  } catch (error) {
    console.error('Error updating medication:', error);
    throw error;
  }
};

/**
 * Stop (soft-delete) a medication — sets status = 'ended'
 * @param {function} getToken - Clerk getToken
 * @param {string} id - Medication UUID
 * @returns {Promise<Object>} The stopped medication object
 */
export const stopMedication = async (getToken, id) => {
  try {
    const token = await getToken();

    const response = await fetch(`${API_BASE_URL}/medications/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status}`);
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to stop medication');
    }

    return data.medication;
  } catch (error) {
    console.error('Error stopping medication:', error);
    throw error;
  }
};